import { UserRoundCheck } from 'lucide-react';

import { EmptyState } from '@/components/EmptyState/EmptyState';
import {
    SegmentedControl,
    SegmentedControlItem,
} from '@/components/SegmentedControl/SegmentedControl';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableNum,
    TableRow,
} from '@/components/Table/Table';
import { type ClientsParams, clientsExportUrl, type ClientsReport } from '@/features/reports/api';
import { exportFileName, inactiveWindows } from '@/features/reports/clients/labels';
import { ClientName } from '@/features/reports/clients/SpendView';
import { CLIENTS_COPY } from '@/features/reports/copy';
import { formatPounds, formatPoundsCompact } from '@/features/reports/format';
import { Definition, ReportCard, Tile } from '@/features/reports/parts';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { formatDate } from '@/lib/format';

const COPY = CLIENTS_COPY.inactive;

/**
 * Clients › Inactive (ALS-87): active clients who have bought before but not in the window ending
 * today, longest quiet first. The window defaults to the setting and the reader can choose another
 * (Franka, 22 Oct 2026); the list is as of today, whatever the period control says.
 */
export function InactiveView({
    report,
    params,
    onWindowChange,
}: {
    report: ClientsReport;
    params: ClientsParams;
    onWindowChange: (days: number | undefined) => void;
}) {
    const { days, defaultDays, atRiskValue, clients } = report.inactive;

    return (
        <>
            <div className="flex flex-wrap items-center gap-3">
                <SegmentedControl
                    aria-label={COPY.window}
                    value={String(days)}
                    // The default is left out of the address, as every report default is.
                    onValueChange={(next) =>
                        onWindowChange(Number(next) === defaultDays ? undefined : Number(next))
                    }
                    className="max-w-full overflow-x-auto"
                >
                    {inactiveWindows(defaultDays).map((d) => (
                        <SegmentedControlItem
                            key={d}
                            value={String(d)}
                            className="pointer-coarse:min-w-11"
                        >
                            {COPY.days(d)}
                        </SegmentedControlItem>
                    ))}
                </SegmentedControl>
            </div>

            <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                <Tile
                    label={COPY.tiles.count}
                    value={String(clients.length)}
                    note={COPY.days(days)}
                    definition={COPY.definition}
                />
                <Tile
                    label={COPY.tiles.atRisk}
                    value={formatPoundsCompact(atRiskValue)}
                    note={CLIENTS_COPY.spend.tiles.atRisk.note(atRiskValue)}
                />
            </div>

            <ReportCard
                title={COPY.card}
                subtitle={COPY.subtitle(days)}
                actions={
                    <>
                        <Definition label={COPY.card}>{COPY.definition}</Definition>
                        <ExportButton
                            url={clientsExportUrl('Inactive', params)}
                            fileName={exportFileName(['inactive', days, 'days'])}
                            disabled={clients.length === 0}
                        />
                    </>
                }
            >
                {clients.length === 0 ? (
                    <EmptyState
                        icon={<UserRoundCheck />}
                        title={COPY.empty.title}
                        body={COPY.empty.body}
                    />
                ) : (
                    <Table stack region={COPY.card} className="table-stack--wide-labels">
                        <TableHeader>
                            <TableRow>
                                <TableHead>{COPY.columns.client}</TableHead>
                                <TableHead>{COPY.columns.lastOrder}</TableHead>
                                <TableHead className="text-right">{COPY.columns.days}</TableHead>
                                <TableHead className="text-right">{COPY.columns.orders}</TableHead>
                                <TableHead className="text-right">
                                    {COPY.columns.lifetime}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {clients.map((c) => (
                                <TableRow key={c.customerId}>
                                    <TableCell data-stack="title">
                                        <ClientName
                                            customerId={c.customerId}
                                            name={c.client}
                                            manager={c.relationshipManager}
                                        />
                                    </TableCell>
                                    <TableCell
                                        data-stack="meta"
                                        data-label={COPY.columns.lastOrder}
                                        className="text-muted-foreground whitespace-nowrap"
                                    >
                                        {formatDate(c.lastOrderOn)}
                                    </TableCell>
                                    <TableNum data-stack="status" className="font-medium">
                                        {COPY.days(c.daysSince)}
                                    </TableNum>
                                    <TableNum data-stack="meta" data-label={COPY.columns.orders}>
                                        {c.orders}
                                    </TableNum>
                                    <TableNum data-stack="meta" data-label={COPY.columns.lifetime}>
                                        {formatPounds(c.lifetime)}
                                    </TableNum>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </ReportCard>
        </>
    );
}
